import { useCallback, useEffect, useState } from "react";
import { Archive, Copy, Loader2, Plus, X } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Healthcare", "Construction", "Engineering", "Hospitality", "Manufacturing", "Logistics", "Agriculture", "Oil & Gas", "Technology", "Security", "Skilled Trades", "Professional Services"];
const EMPLOYMENT_TYPES = ["Full-time", "Contract", "Part-time", "Temporary"];
const JOB_STATUSES = ["draft", "pending_review", "published", "paused", "closed", "rejected", "archived"];
const YES_NO = ["", "Yes", "No"];

const STATUS_STYLES = {
    draft: "border-ink/25 text-ink/50",
    pending_review: "border-champagne/60 text-[#8a7320]",
    published: "border-forest/50 text-forest",
    paused: "border-ink/30 text-ink/55",
    closed: "border-champagne/60 text-[#8a7320]",
    rejected: "border-red-900/30 text-red-900",
    archived: "border-ink/15 text-ink/35",
};

const EMPTY_JOB = {
    title: "", company: "", country: "", city: "", category: "", employment_type: "",
    positions: 1, experience: "", salary_text: "", salary_min: "", salary_max: "",
    accommodation: "", transportation: "", deadline: "", contract_duration: "", start_date: "",
    description: "", responsibilities: "", requirements: "", qualifications: "", benefits: "",
    status: "draft", is_demo: false,
};

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

const Label = ({ children }) => (
    <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">{children}</span>
);

export default function AdminJobs() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // null | { id: string|null, form: {...} }
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        const res = await fetch(`${API}/api/admin/jobs`, { credentials: "include" });
        if (res.ok) setJobs(await res.json());
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user) fetchJobs();
    }, [user, fetchJobs]);

    const openCreate = () => setEditor({ id: null, form: { ...EMPTY_JOB } });
    const openEdit = (job) =>
        setEditor({
            id: job.id,
            form: {
                ...EMPTY_JOB,
                ...job,
                responsibilities: (job.responsibilities || []).join("\n"),
                requirements: (job.requirements || []).join("\n"),
                salary_min: job.salary_min ?? "",
                salary_max: job.salary_max ?? "",
            },
        });

    const setField = (key, value) =>
        setEditor((e) => ({ ...e, form: { ...e.form, [key]: value } }));

    const save = async () => {
        if (saving) return;
        setSaving(true);
        setError("");
        const form = {
            ...editor.form,
            positions: Number(editor.form.positions) || 1,
            salary_min: editor.form.salary_min === "" ? null : Number(editor.form.salary_min),
            salary_max: editor.form.salary_max === "" ? null : Number(editor.form.salary_max),
        };
        try {
            const res = await fetch(
                editor.id ? `${API}/api/admin/jobs/${editor.id}` : `${API}/api/admin/jobs`,
                {
                    method: editor.id ? "PATCH" : "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(form),
                }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                const detail = body.detail;
                throw new Error(
                    typeof detail === "string"
                        ? detail
                        : detail
                          ? Object.values(detail).join(" ")
                          : "Could not save the job."
                );
            }
            setEditor(null);
            fetchJobs();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const archive = async (id) => {
        await fetch(`${API}/api/admin/jobs/${id}`, { method: "DELETE", credentials: "include" });
        fetchJobs();
    };

    const duplicate = async (id) => {
        await fetch(`${API}/api/admin/jobs/${id}/duplicate`, { method: "POST", credentials: "include" });
        fetchJobs();
    };

    if (!user) return <AdminLoading />;
    if (!hasPermission("jobs")) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active="jobs" email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section="jobs" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active="jobs" email={user.email} user={user} onLogout={logout} />

            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-serif text-3xl font-medium tracking-tight">Jobs</h1>
                        <p className="mt-1 text-sm text-ink/55">
                            Create and manage the roles shown on the public jobs page. Only published jobs
                            are visible to candidates.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="job-create-btn"
                        onClick={openCreate}
                        className="inline-flex items-center gap-2 bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep"
                    >
                        <Plus size={15} strokeWidth={1.5} />
                        New Job
                    </button>
                </div>

                <div className="mt-10" data-testid="admin-jobs-list">
                    {loading ? (
                        <div className="flex justify-center py-20"><Loader2 size={22} className="animate-spin text-forest" /></div>
                    ) : jobs.length === 0 ? (
                        <p className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">
                            No jobs yet — create the first one.
                        </p>
                    ) : (
                        jobs.map((job) => (
                            <div
                                key={job.id}
                                data-testid={`admin-job-row-${job.slug}`}
                                className="grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-2 border-t border-ink/10 px-2 py-5 sm:grid-cols-[auto_1fr_auto_auto_auto]"
                            >
                                <span className={`w-fit border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${STATUS_STYLES[job.status]}`}>
                                    {job.status}
                                </span>
                                <button type="button" onClick={() => openEdit(job)} className="min-w-0 text-left">
                                    <span className="block truncate font-serif text-lg font-medium tracking-tight text-ink transition-colors hover:text-forest">
                                        {job.title}
                                    </span>
                                    <span className="block truncate text-xs text-ink/50">
                                        {job.company} · {job.city ? `${job.city}, ` : ""}{job.country} · {job.positions} position{job.positions === 1 ? "" : "s"}
                                        {job.is_demo ? " · demo" : ""}
                                    </span>
                                </button>
                                <span className="hidden text-xs text-ink/45 sm:block">
                                    {job.deadline ? `Deadline ${job.deadline}` : "No deadline"}
                                </span>
                                <button
                                    type="button"
                                    data-testid={`job-edit-${job.slug}`}
                                    onClick={() => openEdit(job)}
                                    className="hidden border border-ink/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-forest hover:text-forest sm:block"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    data-testid={`job-duplicate-${job.slug}`}
                                    onClick={() => duplicate(job.id)}
                                    aria-label={`Duplicate ${job.title}`}
                                    className="hidden border border-ink/15 p-2.5 text-ink/45 transition-colors hover:border-forest hover:text-forest sm:block"
                                >
                                    <Copy size={15} strokeWidth={1.5} />
                                </button>
                                <button
                                    type="button"
                                    data-testid={`job-archive-${job.slug}`}
                                    onClick={() => archive(job.id)}
                                    aria-label={`Archive ${job.title}`}
                                    className="hidden border border-ink/15 p-2.5 text-ink/45 transition-colors hover:border-ink/40 hover:text-ink sm:block"
                                >
                                    <Archive size={15} strokeWidth={1.5} />
                                </button>
                            </div>
                        ))
                    )}
                    <div className="border-t border-ink/10" />
                </div>
            </div>

            {editor && (
                <div className="fixed inset-0 z-50" data-testid="job-editor">
                    <div className="absolute inset-0 bg-night/50" onClick={() => setEditor(null)} />
                    <div className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <h2 className="font-serif text-2xl font-medium tracking-tight">
                                {editor.id ? "Edit Job" : "New Job"}
                            </h2>
                            <button type="button" data-testid="job-editor-close" onClick={() => setEditor(null)} aria-label="Close editor" className="border border-ink/15 p-2 text-ink/60 transition-colors hover:border-forest hover:text-forest">
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="mt-8 grid gap-5 sm:grid-cols-2">
                            <div className="sm:col-span-2"><Label>Job title *</Label><input data-testid="job-form-title" value={editor.form.title} onChange={(e) => setField("title", e.target.value)} className={inputClass} placeholder="e.g. Registered Nurses" /></div>
                            <div><Label>Employer / company *</Label><input data-testid="job-form-company" value={editor.form.company} onChange={(e) => setField("company", e.target.value)} className={inputClass} /></div>
                            <div><Label>Country *</Label><input data-testid="job-form-country" value={editor.form.country} onChange={(e) => setField("country", e.target.value)} className={inputClass} /></div>
                            <div><Label>City</Label><input data-testid="job-form-city" value={editor.form.city} onChange={(e) => setField("city", e.target.value)} className={inputClass} /></div>
                            <div>
                                <Label>Category</Label>
                                <select data-testid="job-form-category" value={editor.form.category} onChange={(e) => setField("category", e.target.value)} className={`${inputClass} cursor-pointer`}>
                                    <option value="">Select category</option>
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Employment type</Label>
                                <select data-testid="job-form-type" value={editor.form.employment_type} onChange={(e) => setField("employment_type", e.target.value)} className={`${inputClass} cursor-pointer`}>
                                    <option value="">Select type</option>
                                    {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div><Label>Number of vacancies</Label><input data-testid="job-form-positions" type="number" min="1" value={editor.form.positions} onChange={(e) => setField("positions", e.target.value)} className={inputClass} /></div>
                            <div><Label>Experience required</Label><input data-testid="job-form-experience" value={editor.form.experience} onChange={(e) => setField("experience", e.target.value)} className={inputClass} placeholder="e.g. 2–5 years" /></div>
                            <div><Label>Salary (display text)</Label><input data-testid="job-form-salary-text" value={editor.form.salary_text} onChange={(e) => setField("salary_text", e.target.value)} className={inputClass} placeholder="e.g. $2,200–$2,800 / month" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Salary min ($/mo)</Label><input data-testid="job-form-salary-min" type="number" min="0" value={editor.form.salary_min} onChange={(e) => setField("salary_min", e.target.value)} className={inputClass} /></div>
                                <div><Label>Salary max ($/mo)</Label><input data-testid="job-form-salary-max" type="number" min="0" value={editor.form.salary_max} onChange={(e) => setField("salary_max", e.target.value)} className={inputClass} /></div>
                            </div>
                            <div>
                                <Label>Accommodation</Label>
                                <select data-testid="job-form-accommodation" value={editor.form.accommodation} onChange={(e) => setField("accommodation", e.target.value)} className={`${inputClass} cursor-pointer`}>
                                    {YES_NO.map((o) => <option key={o} value={o}>{o || "Not specified"}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Transportation</Label>
                                <select data-testid="job-form-transportation" value={editor.form.transportation} onChange={(e) => setField("transportation", e.target.value)} className={`${inputClass} cursor-pointer`}>
                                    {YES_NO.map((o) => <option key={o} value={o}>{o || "Not specified"}</option>)}
                                </select>
                            </div>
                            <div><Label>Application deadline</Label><input data-testid="job-form-deadline" type="date" value={editor.form.deadline} onChange={(e) => setField("deadline", e.target.value)} className={inputClass} /></div>
                            <div><Label>Contract duration</Label><input data-testid="job-form-contract" value={editor.form.contract_duration} onChange={(e) => setField("contract_duration", e.target.value)} className={inputClass} placeholder="e.g. 2 years, renewable" /></div>
                            <div><Label>Expected start date</Label><input data-testid="job-form-start" value={editor.form.start_date} onChange={(e) => setField("start_date", e.target.value)} className={inputClass} placeholder="e.g. March 2027" /></div>
                            <div>
                                <Label>Status</Label>
                                <select data-testid="job-form-status" value={editor.form.status} onChange={(e) => setField("status", e.target.value)} className={`${inputClass} cursor-pointer`}>
                                    {JOB_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                                </select>
                            </div>
                            <label className="flex items-center gap-3 text-sm text-ink/70 sm:col-span-2">
                                <input data-testid="job-form-demo" type="checkbox" checked={editor.form.is_demo} onChange={(e) => setField("is_demo", e.target.checked)} className="h-4 w-4 accent-[#143D29]" />
                                Mark as demo listing (shown with a "Demo listing" label publicly)
                            </label>
                            <div className="sm:col-span-2"><Label>Short description</Label><textarea data-testid="job-form-description" rows={3} value={editor.form.description} onChange={(e) => setField("description", e.target.value)} className={`${inputClass} resize-none`} /></div>
                            <div className="sm:col-span-2"><Label>Responsibilities (one per line)</Label><textarea data-testid="job-form-responsibilities" rows={4} value={editor.form.responsibilities} onChange={(e) => setField("responsibilities", e.target.value)} className={`${inputClass} resize-none`} /></div>
                            <div className="sm:col-span-2"><Label>Requirements (one per line)</Label><textarea data-testid="job-form-requirements" rows={4} value={editor.form.requirements} onChange={(e) => setField("requirements", e.target.value)} className={`${inputClass} resize-none`} /></div>
                            <div className="sm:col-span-2"><Label>Qualifications</Label><textarea data-testid="job-form-qualifications" rows={2} value={editor.form.qualifications} onChange={(e) => setField("qualifications", e.target.value)} className={`${inputClass} resize-none`} /></div>
                            <div className="sm:col-span-2"><Label>Benefits</Label><textarea data-testid="job-form-benefits" rows={3} value={editor.form.benefits} onChange={(e) => setField("benefits", e.target.value)} className={`${inputClass} resize-none`} /></div>
                        </div>

                        {error && <p data-testid="job-form-error" className="mt-6 border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}

                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                type="button"
                                data-testid="job-save-btn"
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-2 bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep disabled:opacity-70"
                            >
                                {saving && <Loader2 size={14} className="animate-spin" />}
                                {editor.id ? "Save Changes" : "Create Job"}
                            </button>
                            <button type="button" onClick={() => setEditor(null)} className="border border-ink/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-ink/40">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
